// ─────────────────────────────────────────────
//  RoleTab — controlled two-tab strip (My Rooms / Full Board)
//  Per the UI-SPEC Tab Component Contract: role="tablist"/"tab",
//  aria-selected, aria-controls, 44px touch target, arrow-key navigation,
//  visible focus ring (never outline:none). Presentational — parent owns value.
// ─────────────────────────────────────────────
import React, { useRef } from "react";
import tokens from "../theme/tokens";

const { color, font } = tokens;
const ACCENT = color.accent.primary; // #1e40af

export default function RoleTab({ tabs = [], value, onChange }) {
  const btnRefs = useRef([]);

  const selectAt = (idx) => {
    const next = tabs[(idx + tabs.length) % tabs.length];
    if (next) {
      onChange?.(next.id);
      btnRefs.current[(idx + tabs.length) % tabs.length]?.focus();
    }
  };

  const onKeyDown = (e, idx) => {
    if (e.key === "ArrowRight") {
      e.preventDefault();
      selectAt(idx + 1);
    } else if (e.key === "ArrowLeft") {
      e.preventDefault();
      selectAt(idx - 1);
    }
  };

  return (
    <div role="tablist" style={styles.list}>
      {tabs.map((tab, idx) => {
        const selected = tab.id === value;
        return (
          <button
            key={tab.id}
            ref={(el) => (btnRefs.current[idx] = el)}
            role="tab"
            id={`tab-${tab.id}`}
            aria-selected={selected ? "true" : "false"}
            aria-controls={`tabpanel-${tab.id}`}
            tabIndex={selected ? 0 : -1}
            onClick={() => onChange?.(tab.id)}
            onKeyDown={(e) => onKeyDown(e, idx)}
            onFocus={(e) => (e.currentTarget.style.outline = `3px solid ${ACCENT}`)}
            onBlur={(e) => (e.currentTarget.style.outline = "none")}
            style={{
              ...styles.tab,
              color: selected ? ACCENT : color.text.secondary,
              fontWeight: selected ? font.weight.bold : font.weight.regular,
              borderBottom: `2px solid ${selected ? ACCENT : "transparent"}`,
            }}
          >
            {tab.label}
          </button>
        );
      })}
    </div>
  );
}

const styles = {
  list: {
    display: "flex",
    fontFamily: font.family,
  },
  tab: {
    height: 44,
    padding: "0 16px",
    fontSize: font.size.body,
    fontFamily: "inherit",
    background: "transparent",
    border: "none",
    cursor: "pointer",
    outlineOffset: "2px",
  },
};
